import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatPhone, isValidEmail } from '../utils/formatters';
import API_BASE from '../config/api';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia',
  'Germany', 'France', 'Spain', 'Italy', 'Japan', 'Brazil', 'Mexico', 'Other',
];

const INPUT_BASE = {
  width: '100%',
  backgroundColor: '#1a1a1a',
  borderRadius: '4px',
  padding: '10px 14px',
  color: '#F5F5F5',
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 400,
  fontSize: '15px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
};

const SELECT_ARROW = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23565656' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")";

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && (
        <label
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 500,
            fontSize: '13px',
            color: '#888888',
          }}
        >
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, focused, onFocus, onBlur, invalid }) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{
        ...INPUT_BASE,
        border: `1px solid ${invalid ? '#BF2634' : focused ? '#39FF14' : '#3a3a3a'}`,
      }}
    />
  );
}

function SelectInput({ value, onChange, options, placeholder, focused, onFocus, onBlur }) {
  return (
    <select
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{
        ...INPUT_BASE,
        border: `1px solid ${focused ? '#39FF14' : '#3a3a3a'}`,
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        color: value ? '#F5F5F5' : '#888888',
        backgroundImage: SELECT_ARROW,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        paddingRight: '32px',
      }}
    >
      <option value="" style={{ backgroundColor: '#1a1a1a', color: '#888888' }}>{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt} style={{ backgroundColor: '#1a1a1a', color: '#F5F5F5' }}>{opt}</option>
      ))}
    </select>
  );
}

export default function AddPersonnelModal({ isOpen, onClose, onSuccess }) {
  const { token } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [cellPhone, setCellPhone] = useState('');
  const [title, setTitle] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('United States');
  const [departments, setDepartments] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [focused, setFocused] = useState({});

  const setFocus = (f, v) => setFocused((p) => ({ ...p, [f]: v }));

  if (!isOpen) return null;

  function resetForm() {
    setFirstName(''); setLastName(''); setPrimaryEmail('');
    setEmailError(null); setCellPhone(''); setTitle('');
    setState(''); setCountry('United States');
    setDepartments(''); setError(null); setIsLoading(false);
  }

  function handleEmailBlur() {
    setFocus('primaryEmail', false);
    if (primaryEmail && !isValidEmail(primaryEmail)) {
      setEmailError('Invalid email format');
    } else {
      setEmailError(null);
    }
  }

  async function handleSubmit(inviteAsUser = false) {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    if (primaryEmail && !isValidEmail(primaryEmail)) {
      setEmailError('Invalid email format');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const body = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        primary_email: primaryEmail,
        cell_phone: cellPhone,
        title,
        state,
        country,
        departments: departments
          ? departments.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      };
      if (inviteAsUser) body.invite_pending = true;
      const res = await axios.post(
        `${API_BASE}/api/personnel`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (inviteAsUser) console.log('INVITE_PENDING', res.data._id);
      onSuccess(res.data);
      onClose();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add person.');
      setIsLoading(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '10px',
          width: '520px',
          maxWidth: '90vw',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 60px rgba(57,255,20,0.08)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#222222',
            padding: '20px 24px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid #3a3a3a',
          }}
        >
          <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '20px', color: '#FFFFFF' }}>
            Add Personnel
          </span>
          <CloseBtn onClick={onClose} />
        </div>

        {/* Body */}
        <form onSubmit={(e) => e.preventDefault()}>
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Row 1 — First Name | Last Name */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="First Name *">
                <TextInput
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  focused={focused.firstName}
                  onFocus={() => setFocus('firstName', true)}
                  onBlur={() => setFocus('firstName', false)}
                />
              </Field>
              <Field label="Last Name *">
                <TextInput
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  focused={focused.lastName}
                  onFocus={() => setFocus('lastName', true)}
                  onBlur={() => setFocus('lastName', false)}
                />
              </Field>
            </div>

            {/* Row 2 — Primary Email */}
            <Field label="Primary Email">
              <TextInput
                value={primaryEmail}
                onChange={(e) => { setPrimaryEmail(e.target.value); if (emailError) setEmailError(null); }}
                placeholder="email@example.com"
                focused={focused.primaryEmail}
                onFocus={() => setFocus('primaryEmail', true)}
                onBlur={handleEmailBlur}
                invalid={!!emailError}
              />
              {emailError && (
                <span style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500,
                  fontStyle: 'italic',
                  fontSize: '13px',
                  color: '#BF2634',
                }}>
                  {emailError}
                </span>
              )}
            </Field>

            {/* Row 3 — Cell Phone | Title */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="Cell Phone">
                <TextInput
                  value={cellPhone}
                  onChange={(e) => setCellPhone(formatPhone(e.target.value))}
                  placeholder="555.000.0000"
                  focused={focused.cellPhone}
                  onFocus={() => setFocus('cellPhone', true)}
                  onBlur={() => setFocus('cellPhone', false)}
                />
              </Field>
              <Field label="Title">
                <TextInput
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Tour Manager"
                  focused={focused.title}
                  onFocus={() => setFocus('title', true)}
                  onBlur={() => setFocus('title', false)}
                />
              </Field>
            </div>

            {/* Row 4 — State | Country */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="State">
                <SelectInput
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  options={US_STATES}
                  placeholder="— State —"
                  focused={focused.state}
                  onFocus={() => setFocus('state', true)}
                  onBlur={() => setFocus('state', false)}
                />
              </Field>
              <Field label="Country">
                <SelectInput
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  options={COUNTRIES}
                  placeholder="— Country —"
                  focused={focused.country}
                  onFocus={() => setFocus('country', true)}
                  onBlur={() => setFocus('country', false)}
                />
              </Field>
            </div>

            {/* Row 5 — Departments */}
            <Field label="Departments">
              <TextInput
                value={departments}
                onChange={(e) => setDepartments(e.target.value)}
                placeholder="e.g. Audio, Lighting"
                focused={focused.departments}
                onFocus={() => setFocus('departments', true)}
                onBlur={() => setFocus('departments', false)}
              />
            </Field>

            {error && (
              <div
                style={{
                  color: '#BF2634',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500,
                  fontStyle: 'italic',
                  fontSize: '14px',
                  backgroundColor: 'rgba(191,38,52,0.1)',
                  borderLeft: '3px solid #BF2634',
                  padding: '10px 14px',
                  borderRadius: '4px',
                }}
              >
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #3a3a3a',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px',
            }}
          >
            <CancelBtn onClick={onClose} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <CreateProfileBtn isLoading={isLoading} onClick={() => handleSubmit(false)} />
              <InviteAsUserBtn isLoading={isLoading} onClick={() => handleSubmit(true)} />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function CloseBtn({ onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: 'none', border: 'none',
        color: h ? '#F5F5F5' : '#565656',
        fontSize: '20px', cursor: 'pointer', lineHeight: 1,
        padding: '0 2px', transition: 'color 0.15s ease',
      }}
    >
      ✕
    </button>
  );
}

function CancelBtn({ onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: 'transparent',
        border: `1px solid ${h ? '#F5F5F5' : '#565656'}`,
        color: '#F5F5F5',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        padding: '10px 20px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'border-color 0.15s ease',
      }}
    >
      Cancel
    </button>
  );
}

function CreateProfileBtn({ isLoading, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      disabled={isLoading}
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        backgroundColor: '#2a2a2a',
        border: `1px solid ${h && !isLoading ? '#F5F5F5' : '#565656'}`,
        color: '#F5F5F5',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        padding: '10px 20px',
        borderRadius: '4px',
        cursor: isLoading ? 'default' : 'pointer',
        opacity: isLoading ? 0.7 : 1,
      }}
    >
      {isLoading ? 'Creating...' : 'Create Profile'}
    </button>
  );
}

function InviteAsUserBtn({ isLoading, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      disabled={isLoading}
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        backgroundColor: h && !isLoading ? '#8B1C26' : '#BF2634',
        color: '#FFFFFF',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        padding: '10px 20px',
        borderRadius: '4px',
        border: 'none',
        cursor: isLoading ? 'default' : 'pointer',
        opacity: isLoading ? 0.7 : 1,
      }}
    >
      {isLoading ? 'Sending...' : 'Invite as User'}
    </button>
  );
}
